/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Animate the slider's horizontal scroll position over a fixed duration.
     * Replaces native `scroll-behavior: smooth`, which Safari 18 runs in ~1 frame
     * on Apple-Silicon Macs, making the slide change look instantaneous.
     * @param {number} targetX - target scrollLeft value
     * @param {number} [duration=600] - animation duration in ms
     */
    animateScrollTo(targetX, duration = 600) {
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

      const start = this.slider.scrollLeft;
      const distance = targetX - start;
      if (distance === 0) return;

      const startTime = performance.now();
      const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        this.slider.scrollLeft = start + distance * easeInOutQuad(progress);
        if (progress < 1) {
          this.scrollRaf = requestAnimationFrame(step);
        } else {
          this.scrollRaf = null;
        }
      };

      this.scrollRaf = requestAnimationFrame(step);
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        const totalOriginal = slides.length;
        slides[0].classList.add('is-visible');

        if (totalOriginal > 1) {
          const clone = slides[0].cloneNode(true);
          clone.classList.remove('is-visible');
          clone.setAttribute('aria-hidden', 'true');
          this.slider.appendChild(clone);
        }

        const allSlides = this.slider.querySelectorAll('.announcement__text');
        let currentIndex = 0;

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const nextIdx = currentIndex + 1;

            if (document.dir === 'rtl') {
              this.animateScrollTo(
                -Math.abs(nextIdx * allSlides[nextIdx].clientWidth)
              );
            } else {
              this.animateScrollTo(allSlides[nextIdx].offsetLeft - 32);
            }

            allSlides[nextIdx].classList.add('is-visible');
            allSlides[currentIndex].classList.remove('is-visible');

            if (nextIdx >= totalOriginal) {
              setTimeout(() => {
                this.slider.style.scrollSnapType = 'none';
                this.slider.style.scrollBehavior = 'auto';
                this.slider.scrollLeft = 0;
                requestAnimationFrame(() => {
                  this.slider.style.scrollSnapType = '';
                  this.slider.style.scrollBehavior = '';
                });
                allSlides[nextIdx].classList.remove('is-visible');
                allSlides[0].classList.add('is-visible');
                currentIndex = 0;
              }, 650);
            } else {
              currentIndex = nextIdx;
            }
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
